import { useNavigate, useParams } from "react-router-dom";
import { PrimaryButton } from "../../components/buttons/PrimaryButton";
import { SecondaryButton } from "../../components/buttons/SecondaryButton";
import Form from "../../components/inputs/Form";
import Header from "../../components/layout/Header";
import { Page, PageBody, PageHeader } from "../../components/layout/Page";
import {
  addIcon,
  backArrow,
  dockIcon,
  nonVisible,
  noPassIcon,
  passIcon,
  save,
  trash,
  visible,
} from "../../assets/icons";
import Card from "../../components/card/Card";
import "./certificate.css";
import { useEffect, useRef, useState } from "react";
import { createRecord, deleteRecord, deleteRecords, listRecords, updateRecord } from "../../server/fetchFunctions";
import { useFindById, useListRecords } from "../../server/hooks/getData";
import { Loader } from "../../components/loader/Loader";
import { uncertainityCalculator } from "./uncertainityCalculator";
import { createCert } from "./createCert";
import { pattern } from "../../lib/pattern";
import { TextInput } from "../../components/inputs/TextInput";
import { formatDate } from "../../lib/dateHelper";
import { ButtonGroup } from "../../components/buttons/ButtonGroup";
import { format } from "../../lib/inputFormatters";
import { DateInput } from "../../components/inputs/Datepicker";

export default function Certificate() {
  document.title = "Eszköz Kalibrálás";
  const navigate = useNavigate();
  const id = useParams().id || "";

  const paramSetRef = useRef<Set<any>>(new Set());

  const { data: job, loading: jobL } = useFindById("jobs", [], id);
  const { data: deviceParams, loading: devParamL } = useListRecords("deviceParam", [job.id], {
    device_id: job.device_id,
  });
  const { data: siPrefix, loading: siPrefixL } = useListRecords("siprefix", [], null);
  const { data: initCertificate, loading: initCertL } = useListRecords("certificate", [job.id], {
    cal_number: `${job.cal_number}`,
  });
  const { data: initCertParams, loading: initCertParamsL } = useListRecords(
    "certificateResults",
    [initCertificate.data[0]],
    {
      certificate_id: initCertificate.data[0]?.id,
    }
  );

  const [etalonParams, setEtalonParams] = useState<any>([]);
  const [certificate, setCertificate] = useState<any>({});
  const [certificateResults, setCertificateResults] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(true);

  //Set loading state
  useEffect(() => {
    if (!jobL && !devParamL && !siPrefixL && !initCertL && !initCertParamsL) setLoading(false);
  }, [jobL, siPrefixL, devParamL, initCertL, initCertParamsL]);

  //Sort etalon parameters
  useEffect(() => {
    const fetch = async () => {
      if ((job.id, deviceParams.data.length > 0)) {
        const etalonSet = new Set(deviceParams.data.map((item: any) => item.etalon_id));

        const etalonIds = Array.from(etalonSet);
        const etalons: any[] = [];
        await Promise.all(
          etalonIds.map(async (id: any) => {
            await listRecords("etalonParam", { etalon_id: id }).then((res) => {
              etalons.push(res.data);
            });
          })
        );

        setEtalonParams(etalons.flat());
      }
    };
    fetch();
  }, [job.id, deviceParams.data]);

  //Create paramSet and certResults state
  useEffect(() => {
    if (initCertParams.data.length > 0) {
      setCertificate({
        ...initCertificate.data[0],
        cal_date: formatDate(initCertificate.data[0].cal_date),
      });
      setCertificateResults(
        initCertParams.data.map((item) => ({
          ...item,
          allowed_deviation: Number(item.allowed_deviation),
          deviation: Number(item.deviation),
          measured_value: Number(item.measured_value) !== 0 ? Number(item.measured_value) : null,
          range: Number(item.range),
          reference_value: Number(item.reference_value),
          resolution: Number(item.resolution),
        }))
      );
    }
    if (Object.keys(paramSetRef.current).length === 0 && initCertParams.data.length > 0) {
      paramSetRef.current = new Set(
        initCertParams.data
          .sort((a: any, b: any) => a.device_function_sort_order - b.device_function_sort_order)
          .map((item: any) => item.device_function)
      );
    }
  }, [initCertParams.data]);

  const handleResultUpdate = async (item: any, value: any, property: string) => {
    const numValue = format.toNumber(value, 9999);

    const reference = property === "reference_value" ? numValue : item.reference_value;
    const measured = property === "measured_value" ? numValue : item.measured_value;

    const uncert = uncertainityCalculator(item, etalonParams, reference, measured, siPrefix);
    setCertificateResults((prev: any) =>
      prev.map((i: any) =>
        i.id === item.id
          ? {
              ...i,
              [property]: numValue,
              allowed_deviation: uncert.allowed_deviation,
              deviation: uncert.deviation,
              deviation_precentage: uncert.deviation_precentage,
              passed: uncert.passed,
              uncertainity: uncert.uncertainity,
            }
          : i
      )
    );
    await updateRecord("certificateResults", item.id, {
      [property]: numValue === "" ? 0 : numValue,
      allowed_deviation: uncert.allowed_deviation,
      deviation: uncert.deviation,
      deviation_precentage: uncert.deviation_precentage,
      passed: uncert.passed,
      uncertainity: uncert.uncertainity,
    });
  };

  const openCertificate = () => {
    window.open(`/jobs/${id}/calibration/pdf`, "_blank");
  };

  const openCalLabel = () => {
    window.open(`/jobs/${id}/calibration/label`, "_blank");
  };

  const openCertificatENG = () => {
    window.open(`/jobs/${id}/calibration/pdf-eng`, "_blank");
  };

  const openCalLabelENG = () => {
    window.open(`/jobs/${id}/calibration/label-eng`, "_blank");
  };

  //page contents

  const main = (
    <PageBody>
      <div className="certificate">
        <div className="certificate_params">
          <Card title="Adatok">
            <div style={{ display: "flex", gap: 10 }}>
              <TextInput
                stateKey="cal_number_prefix"
                inlineLabel="Kalibrálsái szám előtag"
                state={certificate}
                setState={setCertificate}
                pattern={pattern.normal_text}
                required
              />
              <TextInput
                stateKey="cal_number"
                inlineLabel="Kalibrálsái szám"
                state={certificate}
                setState={setCertificate}
                pattern={pattern.number}
                required
              />
              <DateInput
                state={certificate}
                setState={setCertificate}
                stateKey="cal_date"
                inlineLabel="Kalibrálás dátuma"
                labelWidth={170}
                required
              />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <TextInput
                stateKey="dev_description"
                inlineLabel="Kalibrálás tárgya"
                state={certificate}
                setState={setCertificate}
                pattern={pattern.normal_text}
                required
              />
              <TextInput
                stateKey="dev_type"
                inlineLabel="Típus"
                state={certificate}
                setState={setCertificate}
                pattern={pattern.normal_text}
                required
              />
              <TextInput
                stateKey="dev_manufacturer"
                inlineLabel="Gyártó"
                state={certificate}
                setState={setCertificate}
                pattern={pattern.normal_text}
                required
              />
              <TextInput
                stateKey="serial_no"
                inlineLabel="Gyári szám"
                state={certificate}
                setState={setCertificate}
                pattern={pattern.normal_text}
                required
              />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <TextInput
                stateKey="client_name"
                inlineLabel="Megrendelő"
                state={certificate}
                setState={setCertificate}
                pattern={pattern.normal_text}
                required
              />
              <TextInput
                stateKey="client_address"
                inlineLabel="Címe"
                state={certificate}
                setState={setCertificate}
                pattern={pattern.normal_text}
                required
              />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <TextInput
                stateKey="place_of_cal"
                inlineLabel="Kalibrálás helye"
                state={certificate}
                setState={setCertificate}
                pattern={pattern.normal_text}
                required
              />
              <TextInput
                stateKey="employee"
                inlineLabel="Kalibrálást végezte"
                state={certificate}
                setState={setCertificate}
                pattern={pattern.normal_text}
                required
              />
              <TextInput
                stateKey="signer"
                inlineLabel="Jóváhagyta"
                state={certificate}
                setState={setCertificate}
                pattern={pattern.normal_text}
                required
              />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <TextInput
                stateKey="qualify"
                inlineLabel="Minősítést kért"
                state={{ qualify: job.qualify ? "Igen" : "Nem" }}
                setState={setCertificate}
                pattern={pattern.normal_text}
                readonly
              />
              <TextInput
                stateKey="based_on"
                inlineLabel="Minősítés alapja"
                state={certificate}
                setState={setCertificate}
                pattern={pattern.normal_text}
              />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <TextInput
                stateKey="notes"
                inlineLabel="Megjegyzés"
                state={certificate}
                setState={setCertificate}
                pattern={pattern.normal_text}
              />
            </div>
          </Card>
          <Card style={{ width: "100%", justifyItems: "center" }}>
            <h2>Mérési eredmények</h2>
          </Card>
          {[...paramSetRef.current].map((meas_function: any, index) => (
            <Card key={index} title={meas_function}>
              <table>
                <thead>
                  <tr>
                    <th>Méréshatár</th>
                    <th>Helyes érték</th>
                    <th>Mért érték</th>
                    <th>Eltérés</th>
                    <th>Eltérés (%)</th>
                    <th>Megengedett eltérés</th>
                    <th>Mérési bizonytalanság</th>
                    <th>Megfelelt</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {certificateResults
                    .sort((a: any, b: any) => {
                      const aFactor = Number(siPrefix.data.find((i) => i.id === a.si_prefix_id)?.factor || 1);
                      const bFactor = Number(siPrefix.data.find((i) => i.id === b.si_prefix_id)?.factor || 1);

                      const aRange = a.range * aFactor;
                      const bRange = b.range * bFactor;

                      if (aRange !== bRange) {
                        return aRange - bRange;
                      }

                      const aRef = a.reference_value * aFactor;
                      const bRef = b.reference_value * bFactor;

                      return aRef - bRef;
                    })
                    .map(
                      (param: any, index: any) =>
                        param.device_function === meas_function && (
                          <tr key={param.id}>
                            <td>
                              <div className="cert_column">
                                <input type="number" value={param.range} readOnly tabIndex={-1} />
                                <span className="readOnly">{`${param.si_prefix_symbol}${param.unit_symbol}`}</span>
                              </div>
                            </td>
                            <td>
                              <div className="cert_column">
                                <input
                                  type="text"
                                  value={param.reference_value}
                                  onChange={(e) => {
                                    handleResultUpdate(param, e.target.value, "reference_value");
                                  }}
                                />
                                <span>{`${param.si_prefix_symbol}${param.unit_symbol}`}</span>
                              </div>
                            </td>
                            <td>
                              <div className="cert_column">
                                <input
                                  type="text"
                                  value={param.measured_value}
                                  onChange={(e) => {
                                    handleResultUpdate(param, e.target.value, "measured_value");
                                  }}
                                />
                                <span>{`${param.si_prefix_symbol}${param.unit_symbol}`}</span>
                              </div>
                            </td>
                            <td>
                              <div className="cert_column">
                                <input type="text" value={param.deviation} readOnly tabIndex={-1} />
                                <span className="readOnly">{`${param.si_prefix_symbol}${param.unit_symbol}`}</span>
                              </div>
                            </td>
                            <td>
                              <div className="cert_column">
                                <input type="text" value={param.deviation_precentage} readOnly tabIndex={-1} />
                                <span className="readOnly">{`%`}</span>
                              </div>
                            </td>
                            <td>
                              <div className="cert_column">
                                <input type="text" value={param.allowed_deviation} readOnly tabIndex={-1} />
                                <span className="readOnly">{`${param.si_prefix_symbol}${param.unit_symbol}`}</span>
                              </div>
                            </td>
                            <td>
                              <div className="cert_column">
                                <input type="text" value={param.uncertainity} readOnly tabIndex={-1} />
                              </div>
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <span className={param.passed ? "passed" : "failed"}>
                                {param.passed === 1 ? passIcon : noPassIcon}
                              </span>
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <span
                                className="svg"
                                onClick={async () => {
                                  setCertificateResults((prev: any) =>
                                    prev.map((i: any) =>
                                      i.id === param.id
                                        ? {
                                            ...i,
                                            visible: param.visible === 1 ? 0 : 1,
                                          }
                                        : i
                                    )
                                  );
                                  await updateRecord("certificateResults", param.id, {
                                    visible: param.visible === 1 ? 0 : 1,
                                  });
                                }}>
                                {param.visible === 1 ? visible : nonVisible}
                              </span>
                            </td>
                          </tr>
                        )
                    )}
                </tbody>
              </table>
              {/*-----------------------------------*/}
            </Card>
          ))}
        </div>
      </div>
    </PageBody>
  );

  const noCert = (
    <PageBody>
      <Card style={{ textAlign: "center" }}>
        <p>
          Jelenleg nincs kalibrálási bizonyívány ehhez a munkához. <br /> Szeretne létrehozni egyet?
        </p>
        <div style={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
          <div>
            <PrimaryButton
              btn={{
                svgBefore: addIcon,
                text: "Létrehozás",
                onClick: async () => {
                  if (deviceParams.data.length === 0) {
                    alert("Először hozzon létre bizonyítvány sablont ehhez az eszközhöz");
                    return;
                  }
                  setLoading(true);
                  await createCert(job, deviceParams.data, etalonParams, siPrefix);
                  window.location.reload();
                  setLoading(false);
                },
              }}
            />
          </div>
        </div>
      </Card>
    </PageBody>
  );

  return (
    <Page>
      <Form
        onSubmit={async (e) => {
          e.preventDefault();
          setLoading(true);
          deviceParams.data.length !== 0 && (await updateRecord("certificate", certificate.id, certificate));
          setLoading(false);
        }}>
        <Header>
          <ButtonGroup
            btnArray={[
              { svgBefore: dockIcon, text: "Címke", onClick: openCalLabel },
              { svgBefore: dockIcon, text: "Bizonyítvány", onClick: openCertificate },
            ]}
          />
          <ButtonGroup
            btnArray={[
              { svgBefore: dockIcon, text: "Címke | ENG", onClick: openCalLabelENG },
              { svgBefore: dockIcon, text: "Biz. | ENG", onClick: openCertificatENG },
            ]}
          />
          <SecondaryButton
            btn={{
              svgBefore: trash,
              text: "Töröl",
              onClick: async () => {
                setLoading(true);
                await deleteRecord("certificate", certificate.id);
                window.location.reload();
                setLoading(false);
              },
            }}
          />
          <SecondaryButton btn={{ svgBefore: backArrow, text: "Vissza", onClick: () => navigate(-1) }} />
          <PrimaryButton btn={{ svgBefore: save, text: "Ment", type: "submit" }} />
        </Header>
        <PageHeader title="Eszköz Kalibrálása" />
        {loading ? <Loader /> : initCertificate.data[0]?.id ? main : noCert}
      </Form>
    </Page>
  );
}
